import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { debounce, noop } from 'lodash-es';
import { useIntl } from 'react-intl';
import Measure from 'react-measure';
import { useLocation, useMatch } from 'react-router-dom';
import styled from 'styled-components';

import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useFeatureNavigation } from 'hooks/use-feature-navigation';
import { useOrderHref } from 'hooks/use-order-href';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useOffersContext } from 'state/offers';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import CheckoutButtonLink from './checkout-button-link';
import { MobileWebNavigationBorder } from './mobile-web-navigation-border';
import { getNavLinks } from './nav-links';
import { NavigationMobileTab } from './types';
import { generateNavigationMobileTabs } from './utils-sanity';

const IOS_APP_BANNER_HEIGHT = 44;

const AppBannerPadding = styled.div<{ appBannerVisible: boolean }>`
  padding-bottom: ${p => (p.appBannerVisible ? IOS_APP_BANNER_HEIGHT : 0)}px;
  transition: padding 0.05s ease-in-out;
`;

const MobileNavInner = styled.div`
  position: relative;
`;

const MobileWebNavigationContainer = styled.ul`
  background: ${Styles.color.white};
  display: flex;
  justify-content: space-around;
  position: relative;
  bottom: 0;
  width: 100%;
  list-style-type: none;
  margin: 0;
  padding: 0;

  ${Styles.desktop`
    display: none;
  `}
`;

function isIOS(window: Window): boolean {
  // @ts-ignore MSStream is a windows mobile thing, i think
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

// https://stackoverflow.com/questions/47226824/iphonex-and-notch-detection
function isIphoneX(window: Window): true | void {
  // Really basic check for the ios platform
  // https://stackoverflow.com/questions/9038625/detect-if-device-is-ios
  const iOS = isIOS(window);

  // Get the device pixel ratio
  const ratio = window.devicePixelRatio || 1;

  // Define the users device screen dimensions
  const screen = {
    width: window.screen.width * ratio,
    height: window.screen.height * ratio,
  };

  // iPhone X Detection
  if (iOS) {
    // iPhone X & Xs
    if (screen.width === 1125 && screen.height === 2436) {
      return true;
    }
    // iPhone Xr
    if (screen.width === 828 && screen.height === 1792) {
      return true;
    }
    // iPhone Xs Max
    if (screen.width === 1242 && screen.height === 2688) {
      return true;
    }
  }
}

interface IMobileCheckoutButtonProps {
  checkoutRoute: string;
  enableBottomService: boolean;
}

const MobileCheckoutButton: React.FC<IMobileCheckoutButtonProps> = ({
  checkoutRoute,
  enableBottomService,
}) => {
  const baseRouteMatch = useMatch(`${routes.base}*`);
  const storeLocatorMatch = useMatch(`${routes.storeLocator}/*`);
  const menuMatch = useMatch(`${routes.menu}/*`);
  const cartMatch = useMatch(`${routes.cart}/*`);
  const orderConfirmationMatch = useMatch(`${routes.orderConfirmation}/*`);
  const order: any = useOrderContext();
  const offers = useOffersContext();
  const { isAuthenticated } = useAuthContext();
  const cartButton = useRef<HTMLAnchorElement>(null);
  const { logEvent } = useMParticleContext();
  const logMobileCheckoutEvent = useCallback(() => {
    logEvent(CustomEventNames.CHECKOUT_MOBILE, EventTypes.Navigation);
  }, [logEvent]);
  // Do not show checkout button when bottom service mode is enabled
  if (enableBottomService) {
    return null;
  }
  if (!baseRouteMatch) {
    return null;
  }
  // We don't want to show the checkout cart on the store-locator page
  if (storeLocatorMatch) {
    return null;
  }

  if (menuMatch) {
    const menuObjectId = menuMatch.params['*'];
    if (menuObjectId && !menuObjectId.startsWith('section')) {
      return null;
    }
  }

  if (
    ((order.cartEntries.length || offers.selectedOfferCartEntry) && !cartMatch) ||
    orderConfirmationMatch
  ) {
    return (
      <CheckoutButtonLink
        amount={order.calculateCartTotal()}
        to={checkoutRoute}
        ref={cartButton}
        logMobileCheckoutEvent={isAuthenticated() ? logMobileCheckoutEvent : noop}
      />
    );
  }
  return null;
};

interface IMobileWebNavigationBarProps {
  scrollToTop: () => void;
}
const MobileWebNavigationBar: React.FC<IMobileWebNavigationBarProps> = ({ scrollToTop }) => {
  const windowHeight = useRef<number>();
  const iosWindowInitialHeight = useRef<number>();
  const [appBannerVisible, setAppBannerVisible] = useState(false);
  const menuHref = useOrderHref();
  const order: any = useOrderContext();
  const { isStoreOpenAndAvailable } = useStoreContext();
  const { updateAppFooterDimensions } = useUIContext();
  const enableOffers = useFlag(LaunchDarklyFlag.ENABLE_OFFERS);
  const enableBottomService = useFlag(LaunchDarklyFlag.ENABLE_BOTTOM_SERVICE_MODE);
  const enableSanityControlledMobileNav = useFlag(
    LaunchDarklyFlag.ENABLE_SANITY_CONTROLLED_MOBILE_NAV
  );
  const enableRewardsLink = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_REWARDS_NAV_LINK);
  const enableStaticMenu = useFlag(LaunchDarklyFlag.ENABLE_STATIC_MENU);
  const enableAccountInFooter = useFlag(LaunchDarklyFlag.ENABLE_ACCOUNT_IN_FOOTER);

  const { featureNavigationMobile } = useFeatureNavigation();
  const [bottomInset, setBottomInset] = useState(0);
  const { formatMessage } = useIntl();
  const location = useLocation();
  const { logEvent } = useMParticleContext();
  const cartButton = useRef<HTMLAnchorElement>(null);
  const { flags: LDFlags } = useLDContext();

  const logMobileNavOrderEvent = useCallback(() => {
    logEvent(CustomEventNames.MOBILE_NAV_ORDER, EventTypes.Navigation);
  }, [logEvent]);
  const navLinks: NavigationMobileTab[] = useMemo(() => {
    const sanityNavEnabled = enableSanityControlledMobileNav && featureNavigationMobile;

    return sanityNavEnabled
      ? generateNavigationMobileTabs(featureNavigationMobile, LDFlags)
      : getNavLinks({
          menuHref,
          formatMessage,
          enableOffers,
          enableStaticMenu,
          enableRewardsLink,
          enableAccountInFooter,
          logMobileNavOrderEvent,
        });
  }, [
    enableSanityControlledMobileNav,
    featureNavigationMobile,
    LDFlags,
    menuHref,
    formatMessage,
    enableOffers,
    enableStaticMenu,
    enableRewardsLink,
    enableAccountInFooter,
    logMobileNavOrderEvent,
  ]);

  useEffectOnUpdates(() => {
    window.setTimeout(() => {
      cartButton.current && cartButton.current!.focus();
    }, 100);
  }, [order.cartEntries.length]);

  /**
   * This is for app banner on iOS appearing and moving bottom nav off the
   * bottom of the screen.
   */
  useEffect((): (() => void) | void => {
    // Set reference height on component mount.
    iosWindowInitialHeight.current = window.innerHeight;

    const handleResize = debounce(
      () => {
        const windowHeightDifference = iosWindowInitialHeight.current! - window.innerHeight;
        if (windowHeightDifference === IOS_APP_BANNER_HEIGHT) {
          setAppBannerVisible(true);
        } else {
          setAppBannerVisible(false);
        }
      },
      isIOS(window) ? 0 : 100
    );

    if (isIOS(window)) {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  /**
   * This is for handling safe-inset-area-bottom for chrome properly. This
   * is mimicking how safari handles env(safe-area-inset-bottom) by default.
   */
  useEffect((): (() => void) | void => {
    windowHeight.current = window.innerHeight;

    const handleResize = debounce(
      () => {
        if (window.innerHeight > windowHeight.current!) {
          setBottomInset(20);
        } else if (window.innerHeight < windowHeight.current!) {
          setBottomInset(0);
        }
        windowHeight.current = window.innerHeight;
      },
      isIOS(window) ? 0 : 100
    );

    if (isIphoneX(window)) {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  const onClickNavLink = useCallback(
    (href: string) => {
      if (location.pathname === href) {
        // whenever a mobile nav button is pressed when the route already matches,
        // scroll to the top of the view
        scrollToTop();
      }
    },
    [location.pathname, scrollToTop]
  );

  const { serviceMode } = order;

  // TODO: @glengal checkoutRoute is not used. Expected? this is potentially a bug
  let checkoutRoute = routes.cart;
  if (!serviceMode) {
    checkoutRoute = routes.serviceMode;
  } else if (!isStoreOpenAndAvailable) {
    checkoutRoute = routes.storeLocator;
  }

  return (
    <Measure
      onResize={contentRect => {
        if (contentRect.entry) {
          updateAppFooterDimensions({
            top: contentRect.entry.top,
            left: contentRect.entry.left,
            width: contentRect.entry.width,
            height: contentRect.entry.height,
          });
        }
      }}
    >
      {({ measureRef }) => (
        <MobileWebNavigationBorder $bottomInset={bottomInset} bannerHeight="50px" ref={measureRef}>
          <AppBannerPadding appBannerVisible={appBannerVisible}>
            <MobileNavInner>
              <MobileWebNavigationContainer>
                {navLinks.map(({ selectedHrefs, Component }, index) => (
                  // eslint-disable-next-line
                  <li
                    key={index}
                    onClick={() => selectedHrefs[0] && onClickNavLink(selectedHrefs[0])}
                  >
                    <Component />
                  </li>
                ))}
              </MobileWebNavigationContainer>
              {isStoreOpenAndAvailable && (
                <MobileCheckoutButton
                  checkoutRoute={checkoutRoute}
                  enableBottomService={enableBottomService}
                />
              )}
            </MobileNavInner>
          </AppBannerPadding>
        </MobileWebNavigationBorder>
      )}
    </Measure>
  );
};

export default MobileWebNavigationBar;
